/**
 * The case-folding normalizer and the validators built ON it. Membership rule: this leaf holds `normalizeString` and
 * what CALLS it, nothing else -- a caller and its callee have to be co-resident because no floor below `tools/` may
 * value-import a sibling of its own layer.
 *
 * HOMED IN `const/` (moved from `utils/strings/` on 2026-09-27) because that is the floor every caller can
 * reach: `utils/users/buying-power.ts` reads it and is itself a pure `utils/` leaf that may not value-import a `utils/`
 * sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist); while the normalizer sat one floor up that reader was
 * forced to stay a `tools/` leaf and the trade island importing it was a `QUAL-NO-NONFLOW-TOOL-IMPORTS-001` row. Its
 * own value import is one const sibling, so the leaf is const-clean.
 */

import { isNonEmptyString } from '@federa/const/string-guards';

/** Trimmed + lowercased for case-insensitive comparison; '' when input is falsy.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): utils readers that may not import a utils sibling reach it here. */
export function normalizeString(value: string | null | undefined): string {
	if (!isNonEmptyString(value)) return '';
	return value.trim().toLowerCase();
}

/** Normalizes via {@link normalizeString} then validates against `validValues`, returning null on a miss.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): same layer-floor grounds as {@link normalizeString} above. */
export function createStatusNormalizer<T extends string>(validValues: readonly T[]): (input: string) => T | null {
	const validSet = new Set<string>(validValues);
	return (input: string): T | null => {
		const normalized = normalizeString(input);
		if (validSet.has(normalized)) {
			return normalized as T;
		}
		return null;
	};
}
